import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

export interface Filial {
  cnpj: string;   // 14 digitos, sem mascara - vai como header CNPJ
  codigo?: string; // codigo Protheus da filial (ex.: "003001001") - usado
                   // pelo resolverProcesso/U_PnfTesCte que filtra por
                   // filial na planilha REGRAS CTE.
  nome: string;
  sigla?: string;
}

interface FiliaisItemBack {
  cnpj: string;
  codigo: string;
  nome: string;
  sigla?: string;
}

interface FiliaisResp {
  success: boolean;
  data: FiliaisItemBack[];
}

const STORAGE_KEY = 'portalnf_filial_cnpj';

/**
 * Carrega a lista de filiais do backend (GET /portalnf/filiais) e mantem
 * em cache. A filial corrente eh persistida no sessionStorage para que o
 * interceptor consiga enviar o header CNPJ antes mesmo do loadAll() terminar
 * (entre login e primeira resposta do backend).
 */
@Injectable({ providedIn: 'root' })
export class FilialService {
  private _filiais: Filial[] = [];
  private readonly _filiais$ = new BehaviorSubject<Filial[]>([]);
  private readonly _current$ = new BehaviorSubject<Filial | null>(null);

  // Memoiza a chamada de loadAll para evitar disparar /portalnf/filiais
  // multiplas vezes quando varios componentes pedem em paralelo.
  private loadObs$: Observable<Filial[]> | null = null;

  readonly current$: Observable<Filial | null> = this._current$.asObservable();
  readonly filiais$: Observable<Filial[]> = this._filiais$.asObservable();

  constructor(private http: HttpClient) {
    // Semeia _current a partir do sessionStorage. Mantem so o cnpj (nome/sigla
    // ficam vazios ate loadAll completar) - eh suficiente para o interceptor
    // que so usa currentCnpj() ao montar o header.
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      this._current$.next({ cnpj: saved, nome: '', sigla: '' });
    }
  }

  /**
   * Carrega a lista de filiais do backend. Idempotente — chamadas repetidas
   * retornam o cache (shareReplay) e nao re-disparam a request.
   *
   * Apos o backend responder, tenta restaurar a filial corrente a partir do
   * CNPJ salvo no sessionStorage. Se nao houver salvo ou nao bater com nenhuma
   * filial recebida, escolhe a primeira como default (e persiste).
   */
  loadAll(): Observable<Filial[]> {
    if (this._filiais.length > 0) {
      return of(this._filiais);
    }
    if (this.loadObs$) {
      return this.loadObs$;
    }
    this.loadObs$ = this.http.get<FiliaisResp>('portalnf/filiais').pipe(
      map(r => (r.data ?? []).map(f => ({
        cnpj:   (f.cnpj || '').replace(/\D/g, ''),
        codigo: f.codigo || '',
        nome:   f.nome || f.codigo || f.cnpj,
        sigla:  f.sigla || f.codigo || ''
      }))),
      tap(list => {
        this._filiais = list;
        this._filiais$.next(list);
        const saved = sessionStorage.getItem(STORAGE_KEY);
        let chosen = saved ? list.find(x => x.cnpj === saved) : undefined;
        if (!chosen) {
          chosen = list[0];
          if (chosen) {
            sessionStorage.setItem(STORAGE_KEY, chosen.cnpj);
          }
        }
        this._current$.next(chosen ?? null);
      }),
      catchError(err => {
        // Permite retry numa proxima chamada (login expirado, rede caiu, etc).
        this.loadObs$ = null;
        return throwError(() => err);
      }),
      shareReplay(1)
    );
    return this.loadObs$;
  }

  list(): Filial[] {
    return this._filiais;
  }

  current(): Filial | null {
    return this._current$.value;
  }

  currentCnpj(): string | null {
    return this._current$.value?.cnpj ?? null;
  }

  // Lookup codigo Protheus (003001001) por CNPJ (14 digitos). Util pra
  // chamadas backend que filtram por filial (ex.: U_PnfTesCte).
  codigoByCnpj(cnpj: string): string {
    const c = (cnpj || '').replace(/\D/g, '');
    if (!c) { return ''; }
    return this._filiais.find(f => f.cnpj === c)?.codigo ?? '';
  }

  set(cnpj: string): void {
    const f = this._filiais.find(x => x.cnpj === cnpj);
    if (!f) return;
    sessionStorage.setItem(STORAGE_KEY, cnpj);
    this._current$.next(f);
  }

  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    this._current$.next(null);
  }
}
